<script setup lang="ts">
import { ref } from 'vue'
import { mockIdols, mockGroups } from '../mock/gameState'
import type { Idol } from '../game/types'

const selectedIdol = ref<Idol | null>(null)

const statKeys = ['vocal', 'dance', 'charm', 'stamina', 'mental'] as const
type StatKey = typeof statKeys[number]

const statLabels: Record<StatKey, string> = {
  vocal: '歌唱',
  dance: '舞蹈',
  charm: '魅力',
  stamina: '精力',
  mental: '心理',
}

const statusLabels: Record<string, { text: string; class: string }> = {
  active: { text: '良好', class: 'text-accent-cyan bg-accent-cyan' },
  tired: { text: '疲惫', class: 'text-accent-gold bg-accent-gold' },
  injured: { text: '受伤', class: 'text-danger bg-danger' },
  depressed: { text: '低落', class: 'text-accent-purple bg-accent-purple' },
  breakthrough: { text: '突破', class: 'text-primary bg-primary' },
}

function formatNumber(n: number): string {
  if (n >= 10000) return (n / 10000).toFixed(1) + 'w'
  return n.toLocaleString()
}

function getGroupName(idol: Idol): string | null {
  if (!idol.groupId) return null
  const group = mockGroups.find(g => g.id === idol.groupId)
  return group?.name ?? null
}
</script>

<template>
  <div class="max-w-4xl mx-auto">
    <div class="flex items-center justify-between mb-6">
      <h2 class="text-xl font-display font-bold text-text-primary">偶像管理</h2>
      <button class="px-4 py-2 bg-primary text-bg-deep text-sm font-medium rounded-md hover:bg-primary-light transition-colors">
        招募新偶像
      </button>
    </div>

    <!-- 偶像列表 -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div
        v-for="idol in mockIdols"
        :key="idol.id"
        class="bg-bg-surface rounded-lg p-4 border border-accent-purple border-opacity-10 cursor-pointer hover:border-primary hover:border-opacity-40 transition-all"
        @click="selectedIdol = idol"
      >
        <!-- 头部 -->
        <div class="flex items-start gap-3 mb-4">
          <div class="w-16 h-16 rounded-lg bg-gradient-to-br from-bg-base to-bg-elevated flex items-center justify-center text-2xl flex-shrink-0">
            {{ idol.name.charAt(0) }}
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 mb-1">
              <h3 class="text-text-primary font-medium">{{ idol.name }}</h3>
              <span
                class="px-1.5 py-0.5 rounded text-xs"
                :class="statusLabels[idol.status].class + ' bg-opacity-20'"
              >{{ statusLabels[idol.status].text }}</span>
            </div>
            <div class="flex items-center gap-2 text-xs text-text-muted">
              <span
                class="font-medium"
                :class="{
                  'text-rarity-N': idol.rarity === 'N',
                  'text-rarity-R': idol.rarity === 'R',
                  'text-rarity-SR': idol.rarity === 'SR',
                  'text-accent-gold': idol.rarity === 'SSR',
                }"
              >{{ idol.rarity }}</span>
              <span>·</span>
              <span>Lv.{{ idol.level }}</span>
              <span v-if="getGroupName(idol)">· {{ getGroupName(idol) }}</span>
            </div>
          </div>
        </div>

        <!-- 属性条 -->
        <div class="space-y-2">
          <div v-for="statKey in statKeys" :key="statKey" class="flex items-center gap-2">
            <span class="text-text-muted text-xs w-10">{{ statLabels[statKey] }}</span>
            <div class="flex-1 h-1.5 bg-bg-base rounded-full overflow-hidden">
              <div
                class="h-full rounded-full transition-all"
                :class="{
                  'bg-accent-cyan': statKey === 'vocal',
                  'bg-primary': statKey === 'dance',
                  'bg-accent-gold': statKey === 'charm',
                  'bg-accent-purple': statKey === 'stamina',
                  'bg-info': statKey === 'mental',
                }"
                :style="{ width: `${idol.stats[statKey]}%` }"
              />
            </div>
            <span class="text-text-secondary text-xs w-8 text-right tabular-nums">{{ idol.stats[statKey] }}</span>
          </div>
        </div>

        <!-- 底部信息 -->
        <div class="flex items-center justify-between mt-4 pt-3 border-t border-accent-purple border-opacity-10">
          <div class="flex items-center gap-4 text-xs text-text-muted">
            <span>👥 {{ formatNumber(idol.fans) }}</span>
            <span v-if="idol.scandal > 0" class="text-accent-coral">⚠️ 丑闻 {{ idol.scandal }}</span>
          </div>
          <span v-if="idol.graduationRisk" class="text-xs text-danger">退团风险</span>
        </div>
      </div>
    </div>

    <!-- 偶像详情弹窗（占位） -->
    <div
      v-if="selectedIdol"
      class="fixed inset-0 bg-bg-deep bg-opacity-80 flex items-center justify-center z-50 p-4"
      @click.self="selectedIdol = null"
    >
      <div class="bg-bg-surface rounded-lg p-6 max-w-md w-full border border-accent-purple border-opacity-20">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-display font-bold text-text-primary">{{ selectedIdol.name }}</h3>
          <button @click="selectedIdol = null" class="text-text-muted hover:text-text-secondary">✕</button>
        </div>
        <p class="text-text-muted text-sm mb-4">{{ selectedIdol.backstory }}</p>
        <div class="flex gap-2">
          <button class="flex-1 py-2 bg-primary text-bg-deep text-sm font-medium rounded-md">开始训练</button>
          <button class="flex-1 py-2 border border-bg-elevated text-text-muted text-sm rounded-md hover:border-accent-purple">安排休息</button>
        </div>
      </div>
    </div>
  </div>
</template>
